/* eslint-disable pii/no-phone-number */
import { Alert, Box, Grid, Typography } from '@mui/material';
import { map } from 'lodash';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { Button, Checkbox, ContentBox } from '../../components';
import { IRREGULARITIES } from '../../routes';
import {
  fetchGetIrregularityByPlate,
  fetchGetIrregularityListByIds,
  irregularitiesSlice,
  useDispatch,
  useSelector,
} from '../../stores';
import { palette } from '../../theme';
import { handleApiReturn } from '../../utils';
import { IrregularityCard } from './IrregularityCard';
import { IrregularityDetails } from './IrregularityDetails';
import { IrregularityListDetails } from './IrregularityListDetails';
import { CreateVehicleForIrregularityModal } from './modals/CreateVehicleForIrregularityModal';
import { EditCPFModal } from './modals/EditCPFModal';
import { RegularizeListModal } from './modals/RegularizeListModal';
import { RegularizeModal } from './modals/RegularizeModal';
import { SearchIrregularitiesModal } from './modals/SearchIrregularitiesModal';

export const Irregularities: React.FC = () => {
  const dispatch = useDispatch();
  const { driverId } = useSelector((state) => state.profileReducer);
  const { vehicles } = useSelector((state) => state.vehiclesReducer);
  const { pixLoading } = useSelector((state) => state.pixReducer);
  const {
    irregularityList,
    showDetails,
    showListDetails,
    selectedIds,
    irregularitiesLoading,
    irregularityDetails,
    irregularityListNewText,
    searchingByCode,
    searchedPlate,
    allIdsSelected,
  } = useSelector((state) => state.irregularitiesReducer);
  const boxRef = useRef<HTMLDivElement>(null);
  const currentVehicle = searchedPlate
    ? vehicles.find((v) => v.licensePlate === searchedPlate)
    : vehicles[0] ?? null;
  const [currentPage, setCurrentPage] = useState(1);
  const [cardsPerPage] = useState(10);
  const [displayedIds, setDisplayedIds] = useState<
    {
      id: number;
      licensePlate: string;
      vehicleBrand: string | null;
      vehicleCategory: string;
      observation: string | null;
      photoPlate: string;
      registrationDateTime: string;
    }[]
  >([]);

  useEffect(() => {
    if (location.pathname === IRREGULARITIES) {
      handleResetPage();
    }
  }, [location.pathname]);

  useEffect(() => {
    if (showDetails) {
      if (!irregularityDetails || !irregularityDetails.payment) {
        handleBuggedIrregularities();
      }
    }
  }, [showDetails, showListDetails]);

  useEffect(() => {
    // Load the initial cards
    const initialIds = irregularityList.slice(0, cardsPerPage);
    setDisplayedIds(initialIds);
  }, [irregularityList, cardsPerPage]);

  const loadMoreCards = useCallback(() => {
    const nextPage = currentPage + 1;
    const newIds = irregularityList.slice(0, nextPage * cardsPerPage);
    setDisplayedIds(newIds);
    setCurrentPage(nextPage);
  }, [currentPage, irregularityList, cardsPerPage]);

  const handleScroll = useCallback(() => {
    const container = boxRef.current;
    if (container) {
      const { scrollTop, scrollHeight, clientHeight } = container;

      // Load more cards when the user scrolls near the bottom of the container (e.g., 100px from bottom)
      if (scrollHeight - scrollTop - clientHeight < 100) {
        loadMoreCards();
      }
    }
  }, [loadMoreCards]);

  useEffect(() => {
    const box = boxRef.current;
    if (box) {
      box.addEventListener('scroll', handleScroll);
    }
    return () => {
      if (box) {
        box.removeEventListener('scroll', handleScroll);
      }
    };
  }, [handleScroll]);

  const handlePayIrregularities = () => {
    // eslint-disable-next-line lodash/prefer-invoke-map
    const selectedIdsAsString = map(selectedIds, (item) => {
      return item.toString();
    });
    dispatch(
      fetchGetIrregularityListByIds({
        driverId: driverId.toString(),
        irregularityListIds: selectedIdsAsString,
      }),
    ).then((res: any) => {
      if (!res.payload.data.payment) {
        handleBuggedIrregularities();
      } else {
        handleApiReturn(
          res,
          () => {
            dispatch(irregularitiesSlice.actions.toggleShowListDetails());
          },
          null,
        );
      }
    });
  };

  const handleResetPage = () => {
    if (vehicles.length > 0) {
      dispatch(
        fetchGetIrregularityByPlate({
          driverId: driverId.toString(),
          plate: searchedPlate || vehicles[0].licensePlate,
        }),
      );
    } else {
      dispatch(irregularitiesSlice.actions.resetSearchedPlate());
    }
    if (showDetails && !irregularityDetails) {
      dispatch(irregularitiesSlice.actions.toggleShowDetails());
    }
    showListDetails &&
      dispatch(irregularitiesSlice.actions.toggleShowListDetails());
  };

  const handleBuggedIrregularities = () => {
    dispatch(
      irregularitiesSlice.actions.toggleCreateVehicleForIrregularityIsOpen(),
    );
    toast.warn(
      'Irregularidade sem categoria. Por favor cadastre o veículo relacionado à essa irregularidade para continuar',
    );
    if (showDetails) {
      dispatch(irregularitiesSlice.actions.toggleShowDetails());
    }
  };

  const handleCheck = () => {
    let newSelectedIds: any[] = [];
    if (!allIdsSelected) {
      map(irregularityList, (item) => {
        newSelectedIds = [...newSelectedIds, item.id];
      });
      dispatch(irregularitiesSlice.actions.setSelectedIds(newSelectedIds));
      dispatch(irregularitiesSlice.actions.setAllIdsSelected(true));
    } else {
      dispatch(irregularitiesSlice.actions.setSelectedIds(newSelectedIds));
      dispatch(irregularitiesSlice.actions.setAllIdsSelected(false));
    }
  };

  const alertMessageSxWarning = {
    py: 0.25,
    '& .MuiAlert-icon': {
      py: 0,
      mr: 1,
    },
    '& .MuiAlert-message': {
      py: 0,
      fontWeight: 600,
      fontSize: { xs: '0.75rem', sm: '0.8rem' },
      lineHeight: 1.25,
    },
  };

  return (
    <ContentBox loading={irregularitiesLoading || pixLoading}>
      <Box
        sx={{
          flex: '1 1 0%',
          minHeight: 0,
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignSelf: 'stretch',
          overflowY: showDetails || showListDetails ? 'auto' : 'hidden',
          overflowX: 'hidden',
        }}
      >
        {!showDetails && !showListDetails && (
          <Grid
            container
            sx={{
              position: 'relative',
              width: '100%',
              flex: '1 1 0%',
              minHeight: 0,
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'flex-start',
              alignItems: 'stretch',
              gap: 2,
              padding: 2,
            }}
          >
            <Box
              sx={{
                width: '100%',
                maxWidth: 920,
                alignSelf: 'center',
                backgroundColor: palette.grey[100],
                padding: { xs: '4px 10px', sm: '6px 14px' },
                borderRadius: '999px',
                textAlign: 'center',
              }}
            >
              {currentVehicle ? (
                <Typography
                  sx={{
                    fontSize: {
                      xs: '13px',
                      sm: '15px',
                      md: '15px',
                      lg: '17px',
                      xl: '23px',
                    },
                    color: palette.primary.main,
                    lineHeight: 1.2,
                  }}
                >
                  {currentVehicle.category} · {currentVehicle.licensePlate}
                </Typography>
              ) : searchedPlate ? (
                <Typography
                  sx={{
                    fontSize: {
                      xs: '13px',
                      sm: '15px',
                      md: '15px',
                      lg: '17px',
                      xl: '23px',
                    },
                    color: palette.primary.main,
                    lineHeight: 1.2,
                  }}
                >
                  Placa: {searchedPlate}
                </Typography>
              ) : (
                <Typography
                  color={palette.text.secondary}
                  sx={{ fontSize: { xs: '12px', sm: '13px' } }}
                >
                  Busque por placa ou selecione um veículo para ver
                  irregularidades
                </Typography>
              )}
            </Box>

            <Grid
              container
              spacing={2}
              sx={{ width: '100%', maxWidth: 920, alignSelf: 'center' }}
            >
              <Grid item xs={12} sm={4}>
                <Button
                  variant='contained'
                  label='Buscar por código'
                  type='submit'
                  fontSize='13px'
                  onClick={() => {
                    !searchingByCode &&
                      dispatch(
                        irregularitiesSlice.actions.toggleSearchingByCode(),
                      );
                    dispatch(irregularitiesSlice.actions.toggleSearchIsOpen());
                  }}
                  color='primary'
                  sx={{
                    width: '100%',
                    minHeight: 32,
                    padding: '4px 10px',
                    fontSize: '13px',
                    lineHeight: 1.2,
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <Button
                  variant='contained'
                  label='Buscar por placa'
                  type='submit'
                  fontSize='13px'
                  onClick={() => {
                    searchingByCode &&
                      dispatch(
                        irregularitiesSlice.actions.toggleSearchingByCode(),
                      );
                    dispatch(irregularitiesSlice.actions.toggleSearchIsOpen());
                  }}
                  color='primary'
                  sx={{
                    width: '100%',
                    minHeight: 32,
                    padding: '4px 10px',
                    fontSize: '13px',
                    lineHeight: 1.2,
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <Button
                  variant='contained'
                  label='Pagar irregularidades selecionadas'
                  type='submit'
                  fontSize='13px'
                  onClick={handlePayIrregularities}
                  disabled={selectedIds.length === 0}
                  color='secondary'
                  sx={{
                    width: '100%',
                    minHeight: 32,
                    padding: '4px 10px',
                    fontSize: '13px',
                    lineHeight: 1.2,
                  }}
                />
              </Grid>
            </Grid>

            {/* Results area — list scrolls inside card */}
            <Box
              sx={{
                flex: '1 1 0%',
                minHeight: 0,
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'stretch',
                overflow: 'hidden',
              }}
            >
              <Box
                sx={{
                  flex: '1 1 0%',
                  minHeight: 0,
                  width: '100%',
                  height: '100%',
                  borderRadius: '14px',
                  backgroundColor: 'white',
                  boxShadow: '0px 0px 15px rgba(0,0,0,0.1)',
                  overflow: 'hidden',
                  display: 'flex',
                  flexDirection: 'column',
                  padding: '14px',
                }}
              >
                <Box
                  sx={{
                    flexShrink: 0,
                    width: '100%',
                    marginBottom: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    gap: 0.5,
                  }}
                >
                  {irregularityListNewText && (
                    <Alert
                      severity='warning'
                      variant='standard'
                      sx={{
                        width: '100%',
                        mb: 0,
                        borderRadius: 1,
                        alignItems: 'center',
                        textAlign: 'left',
                        ...alertMessageSxWarning,
                      }}
                    >
                      {irregularityListNewText}
                    </Alert>
                  )}
                  <Checkbox
                    label={
                      <Typography
                        color={palette.text.main}
                        sx={{
                          fontSize: { xs: '13px', sm: '14px' },
                          fontWeight: 600,
                          lineHeight: 1.25,
                        }}
                      >
                        Selecionar todas as irregularidades
                      </Typography>
                    }
                    checked={allIdsSelected}
                    onChange={() => {
                      handleCheck();
                    }}
                    sx={{
                      padding: '6px 10px',
                      '& .MuiSvgIcon-root': {
                        fontSize: 24,
                      },
                    }}
                  />
                </Box>
                {irregularityList.length === 0 ? (
                  <Box
                    sx={{
                      flex: 1,
                      minHeight: 0,
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Typography
                      color={palette.text.main}
                      sx={{
                        fontSize: {
                          xs: '12px',
                          sm: '14px',
                          lg: '18px',
                        },
                      }}
                    >
                      Nenhuma irregularidade
                    </Typography>
                  </Box>
                ) : (
                  <Box
                    ref={boxRef}
                    sx={{
                      flex: '1 1 0%',
                      minHeight: 0,
                      width: '100%',
                      height: '100%',
                      overflowY: 'auto',
                      overflowX: 'hidden',
                    }}
                  >
                    <Grid
                      container
                      rowSpacing={2}
                      columnSpacing={{ xs: 1, sm: 2, md: 2 }}
                      sx={{
                        width: '100%',
                        minHeight: '100%',
                        display: 'flex',
                        justifyContent: 'flex-start',
                        alignContent: 'flex-start',
                        marginLeft: 0,
                      }}
                    >
                      {displayedIds.map((item) =>
                        item ? (
                          <IrregularityCard key={item.id} item={item} />
                        ) : null,
                      )}
                    </Grid>
                  </Box>
                )}
              </Box>
            </Box>
          </Grid>
        )}
        {showDetails && !showListDetails && irregularityDetails?.payment && (
          <IrregularityDetails />
        )}
        {!showDetails && showListDetails && <IrregularityListDetails />}
        <SearchIrregularitiesModal />
        <RegularizeModal />
        <RegularizeListModal />
        <CreateVehicleForIrregularityModal
          resetIrregularities={handleResetPage}
        />
        <EditCPFModal />
      </Box>
    </ContentBox>
  );
};
